import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="organization-session-form"
export default class extends Controller {
  static targets = [
    "stepContent", "stepCircle", "stepLabel",
    "prevButton", "nextButton", "submitButton",
    "standardNav", "step4Nav", "prevButtonStep4",
    "draftButton", "templateButton", "draftField", "templateField",
    "recurringCheckbox", "recurringOptions",
    "recurrenceFrequency", "weeklyOptions",
    "templateNameField", "templateNameInput",
    "templateSelect",
    "equipmentInput", "equipmentTags", "equipmentHidden",
    "reviewName", "reviewInstructor", "reviewStart", "reviewDuration",
    "reviewCapacity", "reviewCredits", "reviewRecurring",
    "reviewRecurrenceSummary", "reviewOptionsTags"
  ]

  static values = {
    currentStep: { type: Number, default: 1 },
    totalSteps: { type: Number, default: 4 },
    confirmTemplateText: { type: String, default: 'Confirm Template' },
    templates: { type: Object, default: {} }
  }

  connect() {
    console.log("Organization session form controller connected")

    // Load existing equipment items from hidden field (for form re-renders after validation errors)
    this.equipmentItems = []
    if (this.hasEquipmentHiddenTarget) {
      try {
        const existing = JSON.parse(this.equipmentHiddenTarget.value || '[]')
        if (Array.isArray(existing)) {
          this.equipmentItems = existing
          this.updateEquipmentTags()
        }
      } catch (e) {
        console.log("Could not parse existing equipment:", e)
      }
    }

    this.updateStepUI()

    // If starting at step 4 (e.g., after validation error), populate the review
    if (this.currentStepValue === this.totalStepsValue) {
      this.updateReview()
    }
  }

  // Step Navigation
  nextStep() {
    if (this.validateStep(this.currentStepValue)) {
      if (this.currentStepValue < this.totalStepsValue) {
        this.currentStepValue++
        this.updateStepUI()

        if (this.currentStepValue === this.totalStepsValue) {
          this.updateReview()
        }
      }
    }
  }

  prevStep() {
    if (this.currentStepValue > 1) {
      this.currentStepValue--
      this.updateStepUI()
    }
  }

  updateStepUI() {
    // Hide all steps
    this.stepContentTargets.forEach(el => el.classList.add('hidden'))

    // Show current step
    this.stepContentTargets[this.currentStepValue - 1].classList.remove('hidden')

    // Update progress indicators
    this.stepCircleTargets.forEach((circle, index) => {
      const stepNumber = index + 1
      const label = this.stepLabelTargets[index]

      if (stepNumber < this.currentStepValue) {
        // Completed step
        circle.className = 'relative flex h-8 w-8 items-center justify-center rounded-full bg-indigo-600'
        circle.innerHTML = '<svg class="h-5 w-5 text-white" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/></svg>'
        label.className = 'ml-4 text-sm font-medium text-gray-900'
      } else if (stepNumber === this.currentStepValue) {
        // Current step
        circle.className = 'relative flex h-8 w-8 items-center justify-center rounded-full bg-indigo-600'
        circle.innerHTML = `<span class="text-white font-semibold text-sm">${stepNumber}</span>`
        label.className = 'ml-4 text-sm font-medium text-gray-900'
      } else {
        // Upcoming step
        circle.className = 'relative flex h-8 w-8 items-center justify-center rounded-full border-2 border-gray-300 bg-white'
        circle.innerHTML = `<span class="text-gray-500 font-semibold text-sm">${stepNumber}</span>`
        label.className = 'ml-4 text-sm font-medium text-gray-500'
      }
    })

    // Update buttons
    if (this.hasPrevButtonTarget) {
      this.prevButtonTarget.classList.toggle('hidden', this.currentStepValue === 1)
    }
    if (this.hasNextButtonTarget) {
      // Hide next button on last step
      if (this.currentStepValue === this.totalStepsValue) {
        this.nextButtonTarget.classList.add('hidden')
      } else {
        this.nextButtonTarget.classList.remove('hidden')
      }
    }
    // Toggle between standard nav (steps 1-3) and step 4 nav panel
    const isLastStep = this.currentStepValue === this.totalStepsValue
    console.log('Step:', this.currentStepValue, 'isLastStep:', isLastStep)

    // Use targets if available, fallback to querySelector
    const standardNav = this.hasStandardNavTarget
      ? this.standardNavTarget
      : this.element.querySelector('[data-organization-session-form-target="standardNav"]')

    const step4Nav = this.hasStep4NavTarget
      ? this.step4NavTarget
      : this.element.querySelector('[data-organization-session-form-target="step4Nav"]')

    if (standardNav) {
      if (isLastStep) {
        standardNav.classList.add('hidden')
      } else {
        standardNav.classList.remove('hidden')
      }
    }

    if (step4Nav) {
      if (isLastStep) {
        step4Nav.classList.remove('hidden')
      } else {
        step4Nav.classList.add('hidden')
      }
    }

    // Scroll to top
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  validateStep(step) {
    const form = this.element.querySelector('form')

    if (step === 1) {
      const name = form.querySelector('[name="organization_session[name]"]')
      const instructor = form.querySelector('[name="organization_session[instructor_id]"]')

      if (!name.value.trim()) {
        alert(this.element.dataset.validationClassName || 'Please enter a class name')
        name.focus()
        return false
      }

      if (!instructor.value) {
        alert(this.element.dataset.validationInstructor || 'Please select an instructor')
        instructor.focus()
        return false
      }
    }

    if (step === 2) {
      const startTime = form.querySelector('[name="organization_session[start_time]"]')
      if (!startTime.value) {
        alert(this.element.dataset.validationStartTime || 'Please select a start time')
        startTime.focus()
        return false
      }
    }

    return true
  }

  // Recurring Options
  toggleRecurringOptions() {
    if (this.hasRecurringOptionsTarget) {
      const isRecurring = this.recurringCheckboxTarget.checked
      this.recurringOptionsTarget.classList.toggle('hidden', !isRecurring)
      if (isRecurring && this.hasWeeklyOptionsTarget) {
        this.weeklyOptionsTarget.classList.remove('hidden')
      }
    }
  }

  toggleWeeklyOptions() {
    if (this.hasWeeklyOptionsTarget && this.hasRecurrenceFrequencyTarget) {
      // Always show day-of-week selection for weekly and daily frequencies
      const freq = this.recurrenceFrequencyTarget.value
      this.weeklyOptionsTarget.classList.toggle('hidden', freq === 'monthly')
    }
  }

  // Save as Draft - sets draft field and submits
  saveAsDraft(event) {
    event.preventDefault()

    if (this.hasDraftFieldTarget) {
      this.draftFieldTarget.value = 'true'
    }
    if (this.hasTemplateFieldTarget) {
      this.templateFieldTarget.value = 'false'
    }

    // Submit the form
    const form = this.element.querySelector('form')
    if (form) form.submit()
  }

  // Save as Template - shows template name input or submits
  saveAsTemplate(event) {
    event.preventDefault()

    // Toggle template name field visibility
    if (this.hasTemplateNameFieldTarget) {
      const isHidden = this.templateNameFieldTarget.classList.contains('hidden')

      if (isHidden) {
        // Show the template name input
        this.templateNameFieldTarget.classList.remove('hidden')
        if (this.hasTemplateNameInputTarget) {
          this.templateNameInputTarget.focus()
        }
        // Update button to show checkmark icon
        const svg = this.templateButtonTarget.querySelector('svg')
        if (svg) {
          svg.innerHTML = '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>'
        }
        // Update button text
        const textNode = this.templateButtonTarget.lastChild
        if (textNode && textNode.nodeType === Node.TEXT_NODE) {
          textNode.textContent = ' ' + this.confirmTemplateTextValue
        }
      } else {
        // Submit as template
        if (this.hasTemplateFieldTarget) {
          this.templateFieldTarget.value = 'true'
        }
        if (this.hasDraftFieldTarget) {
          this.draftFieldTarget.value = 'false'
        }

        const form = this.element.querySelector('form')
        if (form) form.submit()
      }
    }
  }

  // Template Selection
  loadTemplate(event) {
    const templateId = event?.detail?.value
    if (!templateId) return

    const template = this.templatesValue[templateId]
    if (template) {
      this.fillFromTemplate(template)
    }
  }

  fillFromTemplate(template) {
    const form = this.element.querySelector('form')

    // Fill plain text/number fields
    const plainFields = {
      'name': template.name,
      'description': template.description || '',
      'duration_minutes': template.duration_minutes,
      'max_capacity': template.max_capacity,
      'credits_required': template.credits_required
    }

    Object.entries(plainFields).forEach(([field, value]) => {
      const input = form.querySelector(`[name="organization_session[${field}]"]`)
      if (input && value !== undefined && value !== null) {
        input.value = value
      }
    })

    // Fill hw-combobox fields (need to update both hidden input AND visible display)
    const comboboxFields = {
      'instructor_id': template.instructor_id,
      'session_type': template.session_type,
      'category': template.category || '',
      'difficulty_level': template.difficulty_level || '',
      'location_id': template.location_id,
      'room_id': template.room_id
    }

    Object.entries(comboboxFields).forEach(([field, value]) => {
      if (value === undefined || value === null || value === '') return
      this.setComboboxValue(form, `organization_session[${field}]`, value)
    })

    // Fill checkboxes
    const waitlistInput = form.querySelector('[name="organization_session[waitlist_enabled]"]')
    if (waitlistInput) waitlistInput.checked = template.waitlist_enabled

    const membersOnlyInput = form.querySelector('[name="organization_session[members_only]"]')
    if (membersOnlyInput) membersOnlyInput.checked = template.members_only

    // Fill equipment
    if (template.equipment_needed && template.equipment_needed.length > 0) {
      this.equipmentItems = template.equipment_needed
      this.updateEquipmentTags()
    }

    // Show notification
    this.showNotification(this.element.dataset.templateLoadedMessage || 'Template loaded successfully')
  }

  setComboboxValue(form, fieldName, value) {
    const hiddenInput = form.querySelector(`[name="${fieldName}"]`)
    if (!hiddenInput) return

    // Set the hidden input value (for form submission)
    hiddenInput.value = value

    // Find the parent hw-combobox container
    const combobox = hiddenInput.closest('fieldset') || hiddenInput.closest('[data-controller*="hw-combobox"]')
    if (!combobox) return

    // Find the visible combobox input
    const visibleInput = combobox.querySelector('[role="combobox"]')
    if (!visibleInput) return

    // Find matching option to get display text
    const option = combobox.querySelector(`[role="option"][data-value="${value}"]`)
    if (option) {
      visibleInput.value = option.dataset.display || option.textContent.trim()
    }
  }

  showNotification(message) {
    const notification = document.createElement('div')
    notification.className = 'fixed top-4 right-4 bg-green-50 border border-green-200 rounded-lg p-4 shadow-lg z-50'
    notification.innerHTML = `
      <div class="flex items-center">
        <svg class="h-5 w-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
        </svg>
        <p class="ml-3 text-sm font-medium text-green-800">${message}</p>
      </div>
    `
    document.body.appendChild(notification)

    setTimeout(() => {
      notification.remove()
    }, 3000)
  }

  // Equipment Management
  addEquipment(event) {
    if (event) event.preventDefault()

    if (!this.hasEquipmentInputTarget) return

    const item = this.equipmentInputTarget.value.trim()

    if (item && !this.equipmentItems.includes(item)) {
      this.equipmentItems.push(item)
      this.updateEquipmentTags()
      this.equipmentInputTarget.value = ''
    }
  }

  addEquipmentOnEnter(event) {
    if (event.key === 'Enter') {
      event.preventDefault()
      this.addEquipment()
    }
  }

  removeEquipment(event) {
    const item = event.currentTarget.dataset.item
    this.equipmentItems = this.equipmentItems.filter(i => i !== item)
    this.updateEquipmentTags()
  }

  updateEquipmentTags() {
    if (!this.hasEquipmentTagsTarget || !this.hasEquipmentHiddenTarget) return

    this.equipmentTagsTarget.innerHTML = this.equipmentItems.map(item => `
      <span class="inline-flex items-center px-3 py-1 rounded-full text-sm bg-gray-100 text-gray-800">
        ${this.escapeHtml(item)}
        <button type="button"
                data-action="click->organization-session-form#removeEquipment"
                data-item="${this.escapeHtml(item)}"
                class="ml-2 text-gray-500 hover:text-gray-700">
          <svg class="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"/>
          </svg>
        </button>
      </span>
    `).join('')

    this.equipmentHiddenTarget.value = JSON.stringify(this.equipmentItems)
  }

  // Review Summary
  updateReview() {
    const form = this.element.querySelector('form')

    // Basic info
    if (this.hasReviewNameTarget) {
      const name = form.querySelector('[name="organization_session[name]"]')
      this.reviewNameTarget.textContent = name ? name.value || '-' : '-'
    }

    if (this.hasReviewInstructorTarget) {
      const instructorHidden = form.querySelector('[name="organization_session[instructor_id]"]')
      const combobox = instructorHidden?.closest('fieldset, [data-controller*="hw-combobox"]')?.querySelector('[role="combobox"]')
      this.reviewInstructorTarget.textContent = combobox?.value || '-'
    }

    if (this.hasReviewStartTarget) {
      const startTime = form.querySelector('[name="organization_session[start_time]"]')
      this.reviewStartTarget.textContent = startTime?.value ? new Date(startTime.value).toLocaleString() : '-'
    }

    if (this.hasReviewDurationTarget) {
      const duration = form.querySelector('[name="organization_session[duration_minutes]"]')
      const minutesText = this.element.dataset.minutesText || 'minutes'
      this.reviewDurationTarget.textContent = duration?.value ? `${duration.value} ${minutesText}` : '-'
    }

    if (this.hasReviewCapacityTarget) {
      const capacity = form.querySelector('[name="organization_session[max_capacity]"]')
      this.reviewCapacityTarget.textContent = capacity?.value || '-'
    }

    if (this.hasReviewCreditsTarget) {
      const credits = form.querySelector('[name="organization_session[credits_required]"]')
      this.reviewCreditsTarget.textContent = credits?.value || '-'
    }

    // Recurring
    const recurring = form.querySelector('[name="organization_session[recurring]"]')?.checked
    if (this.hasReviewRecurringTarget && this.hasReviewRecurrenceSummaryTarget) {
      if (recurring) {
        const frequency = form.querySelector('[name="organization_session[recurrence_frequency]"]')?.value
        const endDate = form.querySelector('[name="organization_session[recurrence_end_date]"]')?.value

        const repeatsText = this.element.dataset.repeatsText || 'Repeats'
        const untilText = this.element.dataset.untilText || 'until'

        let summary = `${repeatsText} ${frequency}`
        if (endDate) {
          summary += ` ${untilText} ${new Date(endDate).toLocaleDateString()}`
        }
        this.reviewRecurrenceSummaryTarget.textContent = summary
        this.reviewRecurringTarget.classList.remove('hidden')
      } else {
        this.reviewRecurringTarget.classList.add('hidden')
      }
    }

    // Options
    if (this.hasReviewOptionsTagsTarget) {
      this.reviewOptionsTagsTarget.innerHTML = ''
      const options = []

      const checkboxData = [
        { name: 'waitlist_enabled', label: this.element.dataset.waitlistLabel || 'Waitlist Enabled' },
        { name: 'members_only', label: this.element.dataset.membersOnlyLabel || 'Members Only' }
      ]

      checkboxData.forEach(({ name, label }) => {
        const checkbox = form.querySelector(`[name="organization_session[${name}]"]`)
        if (checkbox?.checked) options.push(label)
      })

      if (recurring) {
        options.push(this.element.dataset.recurringLabel || 'Recurring')
      }

      if (options.length > 0) {
        options.forEach(option => {
          const tag = document.createElement('span')
          tag.className = 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800'
          tag.textContent = option
          this.reviewOptionsTagsTarget.appendChild(tag)
        })
      } else {
        const noneSpan = document.createElement('span')
        noneSpan.className = 'text-sm text-gray-500'
        noneSpan.textContent = this.element.dataset.noneText || 'None'
        this.reviewOptionsTagsTarget.appendChild(noneSpan)
      }
    }
  }

  // Utility
  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }
}
